import React, { useState, useEffect } from 'react';
import PFOverlay1 from '../../../assets/pathfinder/pfIntroOverlay/PFOverlay1.png'; // Import the image
import PFOverlay2 from '../../../assets/pathfinder/pfIntroOverlay/PFOverlay2.png'; // Placeholder for future use
import PFOverlay3 from '../../../assets/pathfinder/pfIntroOverlay/PFOverlay3.png'; // Placeholder for future use

const introSlidesDefault = [
	{
		title: 'Specialty Application Basics',
		imgSrc: PFOverlay1, // Use the imported variable
		subtitle:
			'Every UK specialty application consists of a number of domains each requiring specific achievements to score maximum points.',
		bullets: [
			'Each domain contributes a defined number of points to your final application.',
			'To reach interview, you must score above a certain threshold of points.',
			'Applications are generally submitted in late Autumn of the year prior.',
		],
	},
	{
		title: 'Pathfinder',
		imgSrc: PFOverlay2, // Use the imported variable
		subtitle:
			'A simple set-by-step roadmap that enables you to achieve maximum points for your selected specialty application.',
		bullets: [
			'Pathfinder shows the optimal steps to achieve maximum points per domain.',
			'Steps are modelled from official application criteria and designed to be actionable and applicable.',
			'Pathfinder contains features to help you keep accountable, encouraged, and on track.',
		],
	},
	{
		title: 'Achieving with Pathfinder',
		imgSrc: PFOverlay3, // Use the imported variable
		subtitle:
			'Pathfinder is designed as a daily/weekly planner to provide consistent bite size motivation for you to progress.',
		bullets: [
			'Detailed descriptions for each step contain all you need to know.',
			'Integrate SMART steps into your personal calendar to keep you on track.',
			'Complete Tasks, Tick Steps, Achieve your goals!',
		],
	},
];

const PathfinderIntroOverlay = ({
	open,
	onClose,
	onFinished,
	slides = introSlidesDefault,
	storageKey = 'pf-intro-dismissed',
}) => {
	const [step, setStep] = useState(0);
	const [dir, setDir] = useState(1); // 1 = forward, -1 = back
	const [animKey, setAnimKey] = useState(0); // used to retrigger CSS animation

	// NEW: Internal state for auto-show behavior if 'open' prop is undefined
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = typeof open !== 'undefined';
	const shouldShow = isControlled ? open : internalOpen;

	// Animation states
	const [render, setRender] = useState(shouldShow);
	const [isClosing, setIsClosing] = useState(false);

	useEffect(() => {
		if (shouldShow) {
			setRender(true);
			setIsClosing(false);
			// Reset on open
			setStep(0);
			setDir(1);
			setAnimKey(0);
		} else if (render && !isClosing) {
			setIsClosing(true);
			const timer = setTimeout(() => {
				setRender(false);
				setIsClosing(false);
			}, 500); // Match CSS animation duration
			return () => clearTimeout(timer);
		}
	}, [shouldShow, render, isClosing]);

	useEffect(() => {
		// If not controlled, check storage on mount to see if we should show
		if (!isControlled) {
			try {
				const dismissed = window.localStorage.getItem(storageKey);
				if (!dismissed) {
					setInternalOpen(true);
				}
			} catch (_) {}
		}
	}, [isControlled, storageKey]);

	if (!render) return null;

	const isLast = step === slides.length - 1;
	const slide = slides[step];

	const persistDismiss = () => {
		if (typeof window === 'undefined') return;
		try {
			window.localStorage.setItem(storageKey, '1');
		} catch (_) {}
	};

	const handleClose = () => {
		persistDismiss();
		if (!isControlled) setInternalOpen(false);
		onClose?.();
	};

	// Close when clicking outside the shell (on the backdrop)
	const handleBackdropMouseDown = (e) => {
		if (isClosing) return;
		if (e.target !== e.currentTarget) return; // ignore clicks inside the shell
		handleClose();
	};

	const handleBackdropTouchStart = (e) => {
		if (isClosing) return;
		if (e.target !== e.currentTarget) return;
		handleClose();
	};

	const triggerAnim = (direction) => {
		setDir(direction);
		// bump key so CSS animation restarts
		setAnimKey((k) => k + 1);
	};

	const handleNext = () => {
		if (!isLast) {
			triggerAnim(1);
			setStep((s) => Math.min(slides.length - 1, s + 1));
		} else {
			onFinished?.();
			handleClose();
		}
	};

	const handleBack = () => {
		if (step === 0) return;
		triggerAnim(-1);
		setStep((s) => Math.max(0, s - 1));
	};

	const translateBase = dir === 1 ? 18 : -18;

	return (
		<div
			className={`pf-intro-overlay ${isClosing ? 'pf-intro-overlay-closing' : ''}`}
			role="dialog"
			aria-modal="true"
			onMouseDown={handleBackdropMouseDown}
			onTouchStart={handleBackdropTouchStart}
		>
			<div className={`pf-intro-shell ${isClosing ? 'pf-intro-shell-closing' : ''}`}>
				<button
					className="pf-intro-close"
					onClick={handleClose}
					aria-label="Close introduction"
				>
					✕
				</button>

				<div className="pf-intro-body">
					{/* HERO PANEL */}
					<div
						key={`hero-${animKey}`}
						className={`pf-intro-hero pf-intro-slide pf-intro-slide-dir-${dir}`}
						style={{
							'--pf-intro-translate': `${translateBase}px`,
						}}
					>
						{slide.imgSrc ? (
							<img
								src={slide.imgSrc}
								alt=""
								className="pf-intro-heroImage"
							/>
						) : (
							<div className="pf-intro-iconCore">PF</div>
						)}
						<div className="pf-intro-gradientOrb pf-intro-orb-top" />
						<div className="pf-intro-gradientOrb pf-intro-orb-bottom" />
					</div>

					{/* COPY PANEL */}
					<div className="pf-intro-copy">
						<h1
							key={`title-${animKey}`}
							className={`pf-intro-title pf-intro-slide pf-intro-slide-dir-${dir}`}
							style={{
								'--pf-intro-translate': `${translateBase}px`,
							}}
						>
							{slide.title}
						</h1>
						<p
							key={`subtitle-${animKey}`}
							className={`pf-intro-subtitle pf-intro-slide pf-intro-slide-dir-${dir}`}
							style={{
								'--pf-intro-translate': `${translateBase}px`,
							}}
						>
							{slide.subtitle}
						</p>
						<ul
							key={`list-${animKey}`}
							className={`pf-intro-list pf-intro-slide pf-intro-slide-dir-${dir}`}
							style={{
								'--pf-intro-translate': `${translateBase}px`,
							}}
						>
							{slide.bullets.map((b, i) => (
								<li key={i} className="pf-intro-listItem">
									<span className="pf-intro-bulletDot" />
									<span>{b}</span>
								</li>
							))}
						</ul>
					</div>
				</div>

				{/* FOOTER */}
				<div className="pf-intro-footer">
					<div className="pf-intro-steps">
						{slides.map((_, i) => (
							<span
								key={i}
								className={`pf-intro-stepDot ${
									i === step ? 'pf-intro-stepDot-active' : ''
								}`}
							/>
						))}
					</div>
					<div className="pf-intro-ctaRow">
						<button
							type="button"
							onClick={handleBack}
							disabled={step === 0}
							className="pf-intro-backBtn"
						>
							Back
						</button>
						<button
							className="pf-intro-nextBtn"
							onClick={handleNext}
						>
							{isLast ? 'Get started' : 'Next'}
						</button>
					</div>
				</div>
			</div>

			{/* Styles local to the intro overlay */}
			<style jsx>{`
				.pf-intro-overlay {
					position: fixed;
					inset: 0;
					z-index: 2050;
					display: flex;
					align-items: center;
					justify-content: center;
					/* Respect fixed header + device safe-area */
					--pf-header-offset: calc(var(--app-header-height, 72px) + env(safe-area-inset-top, 0px));

					padding: 1.5rem;
					padding-top: calc(1.5rem + var(--pf-header-offset));
					background:
						radial-gradient(circle at top, rgba(59,130,246,0.18), transparent 55%),
						radial-gradient(circle at bottom, rgba(236,72,153,0.18), transparent 55%),
						rgba(15,23,42,0.78);
					backdrop-filter: blur(18px);
					opacity: 1;
					animation: pf-overlay-fadein 0.5s ease backwards;
				}
				.pf-intro-overlay-closing {
					animation: none;
					opacity: 0;
					transition: opacity 0.5s ease;
					pointer-events: none;
				}

				@keyframes pf-overlay-fadein {
					from { opacity: 0; }
					to { opacity: 1; }
				}

				.pf-intro-shell {
					position: relative;
					width: min(1200px, 100%);
					max-height: calc(100vh - (3rem + var(--pf-header-offset)));
					min-height: 600px; /* Added to make overlay taller */
					border-radius: 32px;
					background: #f9fafb;
					box-shadow:
						0 26px 80px rgba(15,23,42,0.55),
						0 0 0 1px rgba(148,163,184,0.4);
					padding: 2.5rem 2.75rem 2.1rem;
					display: flex;
					flex-direction: column;
					gap: 1.6rem;
					transform-origin: center center;
					animation: pf-intro-maximize 0.5s cubic-bezier(0.2, 0, 0, 1) forwards;
				}
				
				.pf-intro-shell-closing {
					animation: pf-intro-minimize 0.5s cubic-bezier(0.2, 0, 0, 1) forwards;
				}

				@keyframes pf-intro-maximize {
					0% {
						opacity: 0;
						transform: translate(calc(-50vw + 40px), calc(50vh - 40px)) scale(0.05);
					}
					100% {
						opacity: 1;
						transform: translate(0, 0) scale(1);
					}
				}

				@keyframes pf-intro-minimize {
					0% {
						opacity: 1;
						transform: translate(0, 0) scale(1);
					}
					100% {
						opacity: 0;
						/* Move to bottom-left (approximate FAB position) */
						transform: translate(calc(-50vw + 40px), calc(50vh - 40px)) scale(0.05);
					}
				}

				.pf-intro-close {
					position: absolute;
					top: 1.4rem;
					right: 1.4rem;
					width: 32px;
					height: 32px;
					border-radius: 999px;
					border: none;
					background: rgba(148,163,184,0.16);
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 1.1rem;
					color: #475569;
					cursor: pointer;
					transition: background 0.18s ease, transform 0.18s ease, color 0.18s ease;
				}
				.pf-intro-close:hover {
					background: rgba(148,163,184,0.3);
					transform: translateY(-1px);
					color: #111827;
				}

				.pf-intro-body {
					display: grid;
					grid-template-columns: minmax(0, 620px) minmax(0, 1fr);
					gap: 2.4rem;
					align-items: center;
					flex: 1; /* Added to fill vertical space */
				}

				.pf-intro-hero {
					position: relative;
					aspect-ratio: 1 / 1; /* Changed from 4 / 5 to square */
					border-radius: 26px;
					background:
						radial-gradient(circle at top, #38bdf8, #3b82f6 45%, #1d4ed8 80%);
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.pf-intro-iconCore {
					width: 80px;
					height: 80px;
					border-radius: 28px;
					background: #0f172a;
					color: #f9fafb;
					display: flex;
					align-items: center;
					justify-content: center;
					font-weight: 700;
					letter-spacing: 0.08em;
					font-size: 1.2rem;
					box-shadow: 0 12px 30px rgba(15,23,42,0.3);
					z-index: 2;
				}
				.pf-intro-heroImage {
					width: 90%;
					height: 90%;
					object-fit: contain;
					z-index: 2;
					filter: drop-shadow(0 12px 24px rgba(0,0,0,0.2));
				}
				.pf-intro-gradientOrb {
					position: absolute;
					width: 140%;
					height: 140%;
					filter: blur(40px);
					opacity: 0.9;
					pointer-events: none;
				}
				.pf-intro-orb-top {
					top: -40%;
					left: -10%;
					background:
						radial-gradient(circle, rgba(248,250,252,0.4), transparent 65%);
				}
				.pf-intro-orb-bottom {
					bottom: -45%;
					right: -15%;
					background:
						radial-gradient(circle, rgba(236,72,153,0.5), transparent 70%);
				}

				.pf-intro-copy {
					display: flex;
					flex-direction: column;
					gap: 0.9rem;
				}
				.pf-intro-title {
					margin: 0;
					font-size: 1.9rem;
					font-weight: 600;
					letter-spacing: -0.03em;
					color: #020617;
				}
				.pf-intro-subtitle {
					margin: 0;
					font-size: 1rem;
					color: #4b5563;
				}
				.pf-intro-list {
					margin: 0.5rem 0 0;
					padding: 0;
					list-style: none;
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				}
				.pf-intro-listItem {
					display: flex;
					align-items: flex-start;
					gap: 0.5rem;
					font-size: 0.9rem;
					color: #1f2933;
				}
				.pf-intro-bulletDot {
					margin-top: 0.35rem;
					width: 8px;
					height: 8px;
					border-radius: 999px;
					background:
						linear-gradient(135deg, #6366f1, #ec4899);
					box-shadow: 0 0 0 3px rgba(129,140,248,0.22);
					flex-shrink: 0;
				}

				.pf-intro-footer {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-top: 0.5rem;
				}
				.pf-intro-steps {
					display: inline-flex;
					gap: 0.3rem;
					align-items: center;
				}
				.pf-intro-stepDot {
					width: 8px;
					height: 8px;
					border-radius: 999px;
					background: #e5e7eb;
				}
				.pf-intro-stepDot-active {
					width: 18px;
					background:
						linear-gradient(135deg, #2563eb, #7c3aed);
					box-shadow: 0 0 0 3px rgba(129,140,248,0.35);
				}

				.pf-intro-ctaRow {
					display: flex;
					align-items: center;
					gap: 0.6rem;
				}
				.pf-intro-backBtn {
					min-width: 90px;
					padding: 0.6rem 1rem;
					border-radius: 999px;
					border: none;
					background: #e5e7eb;
					color: #374151;
					font-size: 0.85rem;
					font-weight: 500;
					cursor: pointer;
					opacity: 1;
					transition: opacity 0.15s ease, transform 0.15s ease;
				}
				.pf-intro-backBtn[disabled] {
					cursor: default;
					opacity: 0.6;
					transform: none;
				}
				.pf-intro-nextBtn {
					min-width: 140px;
					padding: 0.65rem 1.5rem;
					border-radius: 999px;
					border: none;
					background: #2563eb;
					color: #f9fafb;
					font-size: 0.9rem;
					font-weight: 600;
					letter-spacing: 0.03em;
					text-transform: uppercase;
					cursor: pointer;
					box-shadow: 0 10px 30px rgba(37,99,235,0.35);
					transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
				}
				.pf-intro-nextBtn:hover {
					background: #1d4ed8;
					transform: translateY(-1px);
					box-shadow: 0 14px 36px rgba(37,99,235,0.45);
				}
				.pf-intro-nextBtn:active {
					transform: translateY(0);
					box-shadow: 0 8px 20px rgba(37,99,235,0.35);
				}

				/* Shared slide animation */
				.pf-intro-slide {
					opacity: 0;
					transform: translateX(var(--pf-intro-translate, 18px));
					animation: pf-intro-slideIn 260ms ease forwards;
				}
				.pf-intro-slide-dir--1 {
					/* reverse direction */
				}
				@keyframes pf-intro-slideIn {
					from {
						opacity: 0;
						transform: translateX(var(--pf-intro-translate, 18px));
					}
					to {
						opacity: 1;
						transform: translateX(0);
					}
				}

				@media (max-width: 768px) {
					.pf-intro-shell {
						padding: 1.6rem 1.4rem 1.5rem;
						border-radius: 22px;
					}
					.pf-intro-body {
						grid-template-columns: minmax(0, 1fr);
						gap: 1.4rem;
					}
					.pf-intro-hero {
						max-width: 260px;
						margin: 0 auto;
					}
					.pf-intro-title {
						font-size: 1.4rem;
					}
					.pf-intro-subtitle {
						font-size: 0.9rem;
					}
					.pf-intro-footer {
						flex-direction: row;
						gap: 0.75rem;
					}
					.pf-intro-nextBtn {
						min-width: 120px;
					}
				}

				@media (prefers-reduced-motion: reduce) {
					.pf-intro-slide {
						animation: none;
						opacity: 1;
						transform: none;
						transition: none;
					}
				}

				/* @media (prefers-color-scheme: dark) {
					.pf-intro-shell {
						background: #020617;
						box-shadow:
							0 26px 80px rgba(0,0,0,0.85),
							0 0 0 1px rgba(30,64,175,0.5);
					}
					.pf-intro-title { color: #e5e7eb; }
					.pf-intro-subtitle { color: #9ca3af; }
					.pf-intro-listItem { color: #cbd5f5; }
				} */
			`}</style>
		</div>
	);
};

export default PathfinderIntroOverlay;
